import { InLifeBreadcrumbs } from '@in-life/features/Breadcrumbs';
import {
  BodyText,
  Card,
  DetailText,
  Heading,
  Skeleton,
  SkeletonBodyText,
} from '@utilitywarehouse/hearth-react';
import SpotConfettiLight from '@utilitywarehouse/hearth-svg-assets/lib/spot-confetti-light.svg';
import { useLayoutEffect, useRef, useState } from 'react';

const TITLE = "You're all done!";
const OVERHANG_RATIO = 0.4;

type ThankYouHeroProps = {
  tariffTitle: string;
  tariffSubtitle: string;
  price: string | undefined;
  isLoading: boolean;
  hasError: boolean;
};

const ThankYouHero = ({
  tariffTitle,
  tariffSubtitle,
  price,
  isLoading,
  hasError,
}: ThankYouHeroProps) => {
  const [overhang, setOverhang] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);

  // this is used to retrieve the height of the card to set the overhang of the banner
  useLayoutEffect(() => {
    const el = cardRef.current;

    if (!el) {
      return;
    }

    const update = () => {
      setOverhang(el.getBoundingClientRect().height * OVERHANG_RATIO);
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      <div className="thank-you-hero">
        <div className="wrapper">
          <InLifeBreadcrumbs variant="white" />
          <Heading
            as="h1"
            size="xl"
            className="thank-you-hero__title"
            marginTop="200"
            marginBottom="500"
          >
            {TITLE}
          </Heading>
          {!hasError && (
            <Card
              className="thank-you-hero__card"
              ref={cardRef}
              style={{ marginBottom: overhang ? `-${overhang}px` : undefined }}
              display="flex"
              direction="column"
              gap="200"
            >
              <Heading as="h2">Your energy switch is underway</Heading>
              <BodyText color="secondary">Your new energy tariff</BodyText>
              {isLoading ? (
                <Skeleton
                  width="200px"
                  height="16px"
                  loadingTitle="Loading tariff name"
                >
                  <SkeletonBodyText style={{ height: '18px' }} />
                </Skeleton>
              ) : (
                <BodyText
                  className="thank-you-hero__card-subtitle"
                  data-testid="inlife-thank-you-tariff"
                >
                  {tariffTitle}
                  {tariffSubtitle && ` (${tariffSubtitle})`}
                </BodyText>
              )}
              <BodyText color="secondary">Your monthly energy payment</BodyText>
              <DetailText
                size="2xl"
                className="thank-you-hero__price thank-you-hero__subtitle"
                data-testid="inlife-thank-you-price"
              >
                {price ? (
                  price
                ) : (
                  <Skeleton
                    width="100px"
                    height="32px"
                    loadingTitle="Loading monthly cost"
                  >
                    <SkeletonBodyText style={{ height: '28px' }} />
                  </Skeleton>
                )}
              </DetailText>
              <img
                src={SpotConfettiLight}
                alt=""
                className="thank-you-hero__card-confetti"
              />
            </Card>
          )}
        </div>
      </div>
      <div
        className="thank-you-hero__overhang"
        style={{ paddingTop: overhang }}
      />
    </div>
  );
};

export { ThankYouHero };
